import axios from "axios"
import { MY_MOJO_API } from "../Config"
import { Document, DocumentType, FullApplication } from "models/FullApplication"
import { captureMessage } from "@sentry/browser"

/**
 * The documents every applicant has to provide before their appointment. "Other" is the catch-all
 * for whatever an expert asks for later, so it isn't one of them.
 */
export const REQUIRED_DOCUMENT_TYPES: DocumentType[] = ["ID", "Income", "BankStatements"]

/**
 * Whether every applicant has provided each required document, or `undefined` while there is
 * nothing to go on: the application hasn't loaded, or it has no applicants to check against.
 *
 * One document of a type is enough, which is what the documents page shows its tick for (see
 * `DocumentStatus`). Documents belong to an applicant, and the documents page asks each of them
 * separately, so a joint application needs both people's before this is done.
 */
export const hasUploadedRequiredDocuments = (
  application: FullApplication | null | undefined
): boolean | undefined => {
  if (!application?.applicationId) return undefined

  const applicants = application.applicants ?? []
  if (applicants.length === 0) return undefined

  const documents = application.metadata?.documents ?? []

  return applicants.every((applicant) =>
    REQUIRED_DOCUMENT_TYPES.every((docType) =>
      documents.some(
        (document) => document.applicantId === applicant.id && document.docType === docType
      )
    )
  )
}

interface DocumentUploaded {
  key: string
  originalFile: {
    name: string
  }
}

export async function UploadDocumentMetaData(
  filesUploaded: DocumentUploaded[],
  applicationId: string,
  applicantId: number,
  docType: string
): Promise<Document[]> {
  // TODO: change the update document metadata endpoint to be a single batch POST

  if (filesUploaded.length < 1) {
    throw Error("Unreachable: UploadDocumentMetaData called without files.")
  }

  let res
  for (const file of filesUploaded) {
    const currentFilename = file.key.split("/")[1]
    const originalFilename = file.originalFile.name

    res = await axios.put(
      `${MY_MOJO_API}/applications/${applicationId}/document?applicantId=${applicantId}&currentFilename=${currentFilename}&originalFilename=${originalFilename}&docType=${docType}`,
      null,
      {
        withCredentials: true,
      }
    )
  }

  const data = res.data as FullApplication
  return data.metadata.documents
}

export async function getOfferDocuments(applicationId: string): Promise<any> {
  try {
    const response = await axios.get(`${MY_MOJO_API}/documents/${applicationId}/offer`, {
      withCredentials: true,
    })
    return response.data
  } catch (error) {
    captureMessage(`Failed to fetch offer documents for applicationId: ${applicationId}`)
    console.error("Failed to fetch offer documents:", error)
    return null
  }
}
